import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import AmbientBackground from "../components/AmbientBackground";
import AboutWorkVisual from "../components/AboutWorkVisual";
import {
  HERO_COPY_TYPE_DELAY_MS,
  HeroTypingLead,
  HeroTypingTitle,
} from "../components/HeroTypingIntro";
import LeadMagnetSection from "../components/LeadMagnetSection";
import MarketingMediaSlot from "../components/MarketingMediaSlot";
import { trackEvent } from "../lib/analytics";
import {
  AnimatedSection,
  ContentPageInner,
  ContentPageShell,
  GlassPanel,
  PrimaryButton,
  SecondaryButton,
  SectionHeading,
  SectionIntro,
  SectionKicker,
  SectionLead,
} from "../styles/contentPage";

function PriorityMiniVisual({ type }) {
  const visualType = type || "integrations";

  return (
    <PriorityVisual aria-hidden="true" data-priority-visual={visualType}>
      {visualType === "agents" ? (
        <svg
          className="mini-icon robot-icon"
          data-priority-icon
          viewBox="0 0 48 48"
          focusable="false"
        >
          <circle className="robot-knob" cx="24" cy="6.6" r="3.2" />
          <rect className="robot-stem" x="22.5" y="9.6" width="3" height="4.4" rx="1.5" />
          <rect className="robot-ear robot-ear-left" x="1.2" y="29.3" width="4.9" height="7.5" rx="2.2" />
          <rect className="robot-ear robot-ear-right" x="41.9" y="29.3" width="4.9" height="7.5" rx="2.2" />
          <path className="robot-silhouette" d="M6.7 42.1V32.5c0-11.3 7.2-17.3 17.3-17.3s17.3 6 17.3 17.3v9.6c0 3.2-2.6 5.1-6.3 5.1H13c-3.7 0-6.3-1.9-6.3-5.1Z" />
          <circle className="robot-eye robot-eye-left" cx="17" cy="31" r="4.45" />
          <circle className="robot-eye robot-eye-right" cx="31" cy="31" r="4.45" />
          <path className="robot-mouth-wave" d="M18.1 40.4h2.4l1-1.5 1.5 2.7 1.6-3.2 1.5 2.7 1.2-1.6h2.6" />
        </svg>
      ) : null}

      {visualType === "dashboards" ? (
        <svg
          className="mini-icon chart-icon"
          data-priority-icon
          viewBox="0 0 48 48"
          focusable="false"
        >
          <path className="chart-axis" d="M10 38h29" />
          <path className="chart-line" d="M10 32c6 0 8-5 13-5 4 0 5 3 9 1 3-1 5-7 8-9" />
          <circle className="chart-point" cx="40" cy="19" r="2.4" />
        </svg>
      ) : null}

      {visualType === "integrations" ? (
        <svg
          className="mini-icon integration-icon"
          data-priority-icon
          viewBox="0 0 48 48"
          focusable="false"
        >
          <rect className="integration-source integration-source-a" x="1.6" y="7.6" width="13.9" height="6.2" rx="2.2" />
          <rect className="integration-source integration-source-b" x="1.6" y="19.05" width="13.9" height="6.2" rx="2.2" />
          <rect className="integration-source integration-source-c" x="1.6" y="30.5" width="13.9" height="6.2" rx="2.2" />
          <rect className="integration-core" x="21" y="16" width="7.8" height="16" rx="2.7" />
          <rect className="integration-output integration-output-a" x="34.3" y="7.6" width="10.2" height="10" rx="2.8" />
          <rect className="integration-output integration-output-b" x="34.3" y="26.7" width="10.2" height="10" rx="2.8" />
          <path className="integration-output-link" d="M39.4 17.6v9.1" />
          <path className="integration-track integration-track-in" d="M15.5 22.15h5.5" />
          <path className="integration-track integration-track-out-a" d="M28.8 20c3.3 0 2.2-7.4 5.5-7.4" />
          <path className="integration-track integration-track-out-b" d="M28.8 28c3.3 0 2.2 3.7 5.5 3.7" />
          <path className="integration-data-flow integration-data-flow-in" d="M15.5 22.15h5.5" />
          <path className="integration-data-flow integration-data-flow-out-a" d="M28.8 20c3.3 0 2.2-7.4 5.5-7.4" />
          <path className="integration-data-flow integration-data-flow-out-b" d="M28.8 28c3.3 0 2.2 3.7 5.5 3.7" />
          <path className="integration-core-lines" d="M23.4 20h3M23.4 24h3M23.4 28h3" />
        </svg>
      ) : null}
    </PriorityVisual>
  );
}

function CredibilityMiniVisual({ index }) {
  if (index === 0) {
    return (
      <CredibilityVisual aria-hidden="true" data-credibility-visual="direction">
        <svg viewBox="0 0 56 56" focusable="false">
          <path className="credibility-frame" d="M13 40V16H43" />
          <path className="credibility-route-muted" d="M15 38C24 38 22 25 31 25H42" />
          <path className="credibility-route-live" d="M15 38C24 38 22 25 31 25H42" />
          <path className="credibility-arrow" d="M38 20L43 25L38 30" />
        </svg>
      </CredibilityVisual>
    );
  }

  if (index === 1) {
    return (
      <CredibilityVisual aria-hidden="true" data-credibility-visual="proof">
        <svg viewBox="0 0 56 56" focusable="false">
          <path className="credibility-frame" d="M12 42H44" />
          <rect className="credibility-proof-bar credibility-proof-bar-a" x="15" y="30" width="6" height="12" rx="2" />
          <rect className="credibility-proof-bar credibility-proof-bar-b" x="25" y="24" width="6" height="18" rx="2" />
          <rect className="credibility-proof-bar credibility-proof-bar-c" x="35" y="18" width="6" height="24" rx="2" />
          <path className="credibility-proof-check" d="M15 18L21 24L31 13" />
        </svg>
      </CredibilityVisual>
    );
  }

  return (
    <CredibilityVisual aria-hidden="true" data-credibility-visual="execution">
      <svg viewBox="0 0 56 56" focusable="false">
        <rect className="credibility-system" x="9" y="15" width="15" height="26" rx="5" />
        <rect className="credibility-system" x="32" y="15" width="15" height="26" rx="5" />
        <path className="credibility-sync-track" d="M24 28H32" />
        <path className="credibility-sync-live" d="M24 28H32" />
        <path className="credibility-system-line" d="M13 22H20M13 28H20M36 22H43M36 28H43" />
      </svg>
    </CredibilityVisual>
  );
}

export default function ServicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const data = t("servicesOverview", { returnObjects: true });
  const pillarItems = Array.isArray(data.pillars?.items) ? data.pillars.items : [];
  const credibilityItems = Array.isArray(data.credibility?.items)
    ? data.credibility.items
    : [];
  const credibilityRouteSteps = Array.isArray(data.credibility?.routeSteps)
    ? data.credibility.routeSteps
    : [];
  const proofItems = Array.isArray(data.proof?.items) ? data.proof.items : [];
  const heroLead = Array.isArray(data.hero?.lead) ? data.hero.lead : [];
  const mediaSlotLabel = t("aboutPage.media.slot");

  const goTo = (href, source, label, externalUrl) => {
    trackEvent("cta_strategy_call_click", {
      source,
      destination: externalUrl || href,
      label,
      external: Boolean(externalUrl),
    });

    if (externalUrl) {
      window.open(externalUrl, "_blank", "noopener,noreferrer");
      return;
    }

    navigate(href);
  };

  return (
    <Wrap className="page-container">
      <AmbientBackground />
      <Inner>
        <AnimatedSection $i={0}>
          <HeroGrid data-services-hero-grid data-priority-align="center">
            <HeroColumn>
              <TypingHeroTitle data-services-hero-title-typing>
                {data.hero?.title}
              </TypingHeroTitle>
              <HeroRichLead
                data-services-hero-copy-typing
                text={heroLead}
                separator={"\n\n"}
                initialDelayMs={HERO_COPY_TYPE_DELAY_MS}
              />
              <HeroActions>
                <PrimaryButton
                  onClick={() =>
                    goTo("/contact", "services-hero-primary", data.hero?.primaryCta)
                  }
                >
                  {data.hero?.primaryCta}
                </PrimaryButton>
                <SecondaryButton
                  onClick={() =>
                    goTo("/work", "services-hero-secondary", data.hero?.secondaryCta)
                  }
                >
                  {data.hero?.secondaryCta}
                </SecondaryButton>
              </HeroActions>
            </HeroColumn>

            <PriorityPanel data-services-priority-panel>
              <PriorityHeader>
                <PriorityKicker>{data.hero?.priorityTitle}</PriorityKicker>
              </PriorityHeader>
              <PriorityList>
                {pillarItems.map((item) => (
                  <PriorityButton
                    key={item.href}
                    type="button"
                    data-priority-option
                    onClick={() =>
                      goTo(item.href, "services-priority", item.title)
                    }
                  >
                    <PriorityCopy>
                      <span>{item.question}</span>
                      <strong>{item.title}</strong>
                    </PriorityCopy>
                    <PriorityMiniVisual type={item.visual} />
                  </PriorityButton>
                ))}
              </PriorityList>
            </PriorityPanel>
          </HeroGrid>
        </AnimatedSection>

        <AnimatedSection $i={1}>
          <SectionIntro>
            <SectionKicker>{data.pillars?.title}</SectionKicker>
            <SectionHeading>{data.pillars?.heading}</SectionHeading>
            <SectionLead>{data.pillars?.lead}</SectionLead>
          </SectionIntro>

          <PillarsGrid data-pillars-grid>
            {pillarItems.map((item) => (
              <PillarCard
                key={item.href}
                as="button"
                type="button"
                data-pillar-card
                onClick={() => goTo(item.href, "services-pillars", item.title)}
              >
                <PillarVisualFrame
                  data-pillar-visual-frame
                  data-visual-align="fill"
                >
                  {item.visual ? (
                    <PillarVisual
                      data-pillar-visual="true"
                      variant={item.visual}
                      title={item.alt || item.title}
                      aspect="4 / 3"
                    />
                  ) : (
                    <PillarMedia
                      data-pillar-visual="true"
                      src={item.image}
                      alt={item.alt}
                      label={mediaSlotLabel}
                      hint={item.placeholder}
                      aspect="16 / 12"
                    />
                  )}
                </PillarVisualFrame>
                <PillarBody>
                  <PillarLabel>{item.label}</PillarLabel>
                  <PillarTitle>{item.title}</PillarTitle>
                  <PillarText>{item.description}</PillarText>
                  <PillarOutcome>{item.outcome}</PillarOutcome>
                  <PillarLink>{item.cta} →</PillarLink>
                </PillarBody>
              </PillarCard>
            ))}
          </PillarsGrid>
        </AnimatedSection>

        <AnimatedSection $i={2}>
          <CredibilityPanel>
            <CredibilityCopy>
              <SectionKicker>{data.credibility?.title}</SectionKicker>
              <SectionHeading>{data.credibility?.heading}</SectionHeading>
              <SectionLead>{data.credibility?.lead}</SectionLead>
              <CredibilityRouteVisual aria-hidden="true" data-credibility-route-visual>
                <svg viewBox="0 0 320 98" focusable="false">
                  <path className="route-track" d="M34 46H116C141 46 139 23 160 23C181 23 179 46 204 46H286" />
                  <path className="route-flow" d="M34 46H116C141 46 139 23 160 23C181 23 179 46 204 46H286" />
                  <rect className="route-node route-node-a" x="20" y="32" width="28" height="28" rx="8" />
                  <rect className="route-node route-node-b" x="140" y="3" width="40" height="40" rx="12" />
                  <rect className="route-node route-node-c" x="272" y="32" width="28" height="28" rx="8" />
                  <path className="route-backlog-mark" d="M28 41H40M28 49H36" />
                  <path className="route-live-mark" d="M150 22L158 30L171 13" />
                  <path className="route-system-mark" d="M280 41H292M280 49H288" />
                </svg>
                <CredibilityRouteLabels>
                  {credibilityRouteSteps.map((label) => (
                    <span key={label} data-credibility-route-step>{label}</span>
                  ))}
                </CredibilityRouteLabels>
              </CredibilityRouteVisual>
            </CredibilityCopy>
            <CredibilityList>
              {credibilityItems.map((item, index) => (
                <CredibilityItem key={item.title} data-credibility-step>
                  <CredibilityStepMeta>
                    <CredibilityMiniVisual index={index} />
                    <CredibilityStepNumber>{String(index + 1).padStart(2, "0")}</CredibilityStepNumber>
                  </CredibilityStepMeta>
                  <CredibilityItemCopy>
                    <CredibilityTitle>{item.title}</CredibilityTitle>
                    <CredibilityText>{item.description}</CredibilityText>
                  </CredibilityItemCopy>
                </CredibilityItem>
              ))}
            </CredibilityList>
          </CredibilityPanel>
        </AnimatedSection>

        <AnimatedSection $i={3}>
          <LeadMagnetSection data={data.leadMagnet} pageKey="services" />
        </AnimatedSection>

        <AnimatedSection $i={4}>
          <SectionIntro>
            <SectionKicker>{data.proof?.title}</SectionKicker>
            <SectionHeading>{data.proof?.heading}</SectionHeading>
            <SectionLead>{data.proof?.lead}</SectionLead>
          </SectionIntro>

          <ProofStack>
            {proofItems.map((item, index) => (
              <ProofRow
                key={item.title}
                type="button"
                $reverse={index % 2 === 1}
                onClick={() =>
                  goTo(item.href || "/work", "services-proof", item.title, item.externalUrl)
                }
              >
                <ProofMedia
                  src={item.image}
                  alt={item.alt}
                  label={mediaSlotLabel}
                  hint={item.placeholder}
                  aspect="16 / 10"
                  objectPosition={item.position || "center"}
                  objectFit={item.objectFit || "cover"}
                  objectScale={item.objectScale || 1}
                />
                <ProofCopy>
                  <ProofLabelRow>
                    <ProofLabel>{item.label}</ProofLabel>
                    {item.externalUrl ? <ProofExternal>{data.proof?.externalLabel}</ProofExternal> : null}
                  </ProofLabelRow>
                  <ProofTitle>{item.title}</ProofTitle>
                  <ProofText>{item.description}</ProofText>
                </ProofCopy>
              </ProofRow>
            ))}
          </ProofStack>
        </AnimatedSection>

        <AnimatedSection $i={5}>
          <FinalPanel>
            <FinalCopy>
              {data.cta?.title ? <SectionKicker>{data.cta.title}</SectionKicker> : null}
              <FinalHeading>{data.cta?.heading}</FinalHeading>
              <FinalText>{data.cta?.text}</FinalText>
            </FinalCopy>
            <FinalActions>
              <PrimaryButton
                onClick={() => goTo("/contact", "services-final-primary", data.cta?.primary)}
              >
                {data.cta?.primary}
              </PrimaryButton>
              <SecondaryButton
                onClick={() => goTo("/work", "services-final-secondary", data.cta?.secondary)}
              >
                {data.cta?.secondary}
              </SecondaryButton>
            </FinalActions>
          </FinalPanel>
        </AnimatedSection>
      </Inner>
    </Wrap>
  );
}

const Wrap = styled(ContentPageShell)``;

const Inner = styled(ContentPageInner)``;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(300px, 0.92fr);
  gap: clamp(28px, 4vw, 56px);
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const HeroColumn = styled.div`
  display: grid;
  gap: clamp(18px, 2vw, 26px);
`;

const TypingHeroTitle = styled(HeroTypingTitle)`
  max-width: 100%;
`;

const HeroRichLead = styled(HeroTypingLead)`
  max-width: 44rem;
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;

const iconBreathe = keyframes`
  0%, 100% { transform: translateY(0); opacity: 0.78; }
  50% { transform: translateY(-1px); opacity: 0.98; }
`;

const robotBlink = keyframes`
  0%, 88%, 100% { transform: scaleY(1); }
  93% { transform: scaleY(0.48); }
`;

const robotSpeak = keyframes`
  0%, 100% { transform: scaleY(0.72); opacity: 0.58; }
  42% { transform: scaleY(1.18); opacity: 0.9; }
  68% { transform: scaleY(0.9); opacity: 0.72; }
`;

const chartDraw = keyframes`
  0% { stroke-dashoffset: 72; opacity: 0.42; }
  46%, 78% { stroke-dashoffset: 0; opacity: 0.98; }
  100% { stroke-dashoffset: 0; opacity: 0.74; }
`;

const integrationFlow = keyframes`
  0% { stroke-dashoffset: 34; opacity: 0.08; }
  16%, 78% { opacity: 0.92; }
  100% { stroke-dashoffset: 0; opacity: 0.12; }
`;

const integrationCoreSignal = keyframes`
  0%, 100% { opacity: 0.34; }
  42% { opacity: 0.92; }
`;

const credibilityRouteFlow = keyframes`
  0% { stroke-dashoffset: 190; opacity: 0.16; }
  20%, 76% { opacity: 0.92; }
  100% { stroke-dashoffset: 0; opacity: 0.22; }
`;

const credibilityLineDraw = keyframes`
  0% { stroke-dashoffset: 78; opacity: 0.28; }
  42%, 72% { stroke-dashoffset: 0; opacity: 0.92; }
  100% { stroke-dashoffset: 0; opacity: 0.58; }
`;

const credibilityBarLift = keyframes`
  0%, 100% { transform: scaleY(0.58); opacity: 0.48; }
  45%, 72% { transform: scaleY(1); opacity: 0.88; }
`;

const credibilitySyncMove = keyframes`
  0% { stroke-dashoffset: 22; opacity: 0.18; }
  30%, 78% { opacity: 0.9; }
  100% { stroke-dashoffset: 0; opacity: 0.24; }
`;

const PriorityPanel = styled(GlassPanel)`
  align-self: center;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: grid;
  gap: 16px;
  padding: clamp(22px, 3vw, 28px);
  border-radius: 30px;
  background:
    radial-gradient(circle at 12% 4%, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0) 34%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.02)),
    rgba(7, 8, 10, 0.58);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 28px 82px rgba(0, 0, 0, 0.34);

  &::before {
    content: "";
    position: absolute;
    inset: 1px;
    border-radius: inherit;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.06), transparent 34%),
      linear-gradient(90deg, rgba(255, 255, 255, 0.035), transparent 28%);
    opacity: 0.74;
    pointer-events: none;
    z-index: -1;
  }

  @media (max-width: 1100px) {
    align-self: stretch;
  }
`;

const PriorityHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 18px;
  position: relative;
  z-index: 1;
`;

const PriorityKicker = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.56);
`;

const PriorityList = styled.div`
  display: grid;
  gap: 14px;
  position: relative;
  z-index: 1;
`;

const PriorityCopy = styled.span`
  display: grid;
  gap: 7px;
  min-width: 0;
  position: relative;
  z-index: 1;

  span {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.55);
  }

  strong {
    font-size: clamp(19px, 2vw, 23px);
    line-height: 1.13;
    font-weight: 750;
  }
`;

const PriorityVisual = styled.span`
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  justify-self: end;
  width: 48px;
  height: 48px;
  color: rgba(255, 255, 255, 0.76);
  transition: color 0.18s ease, transform 0.18s ease;

  .mini-icon {
    width: 42px;
    height: 42px;
    overflow: visible;
    animation: ${iconBreathe} 3.8s ease-in-out infinite;
  }

  .robot-silhouette,
  .robot-knob,
  .robot-stem,
  .robot-ear {
    fill: currentColor;
    opacity: 0.82;
  }

  .robot-eye {
    fill: rgba(7, 8, 10, 0.94);
  }

  .robot-eye {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${robotBlink} 4s ease-in-out infinite;
  }

  .robot-eye-right {
    animation-delay: 0.04s;
  }

  .robot-mouth-wave {
    fill: none;
    stroke: rgba(7, 8, 10, 0.86);
    stroke-width: 1.75;
    stroke-linecap: round;
    stroke-linejoin: round;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${robotSpeak} 1.35s ease-in-out infinite;
  }

  .chart-axis {
    fill: none;
    stroke: rgba(255, 255, 255, 0.22);
    stroke-width: 2;
    stroke-linecap: round;
  }

  .chart-line {
    fill: none;
    stroke: rgba(255, 255, 255, 0.88);
    stroke-width: 3.2;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 72;
    animation: ${chartDraw} 4.2s ease-in-out infinite;
  }

  .chart-point {
    fill: rgba(255, 255, 255, 0.86);
  }

  .integration-track,
  .integration-data-flow,
  .integration-output-link,
  .integration-core-lines {
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .integration-source,
  .integration-core,
  .integration-output {
    fill: rgba(255, 255, 255, 0.08);
    stroke: currentColor;
    stroke-width: 1.9;
    opacity: 0.74;
  }

  .integration-core {
    fill: rgba(255, 255, 255, 0.14);
    opacity: 0.9;
  }

  .integration-track {
    opacity: 0.28;
  }

  .integration-data-flow {
    opacity: 0.86;
    stroke: currentColor;
    stroke-width: 2.45;
    stroke-linecap: butt;
    stroke-dasharray: 4.8 34;
    animation: ${integrationFlow} 2.15s linear infinite;
  }

  .integration-data-flow-out-a {
    animation-delay: 0.34s;
  }

  .integration-data-flow-out-b {
    animation-delay: 0.54s;
  }

  .integration-core-lines {
    opacity: 0.58;
    stroke-width: 1.45;
    animation: ${integrationCoreSignal} 1.55s ease-in-out infinite;
  }

  .integration-output-link {
    opacity: 0.72;
    stroke-width: 1.55;
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation: none !important;
      transition: none !important;
    }
  }
`;

const PriorityButton = styled.button`
  position: relative;
  isolation: isolate;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: clamp(12px, 1.5vw, 18px);
  min-height: 88px;
  padding: 14px 16px 14px 20px;
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background:
    radial-gradient(circle at 0% 0%, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0) 36%),
    rgba(255, 255, 255, 0.032);
  color: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background:
      linear-gradient(90deg, rgba(255, 255, 255, 0.09), transparent 34%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.06), transparent);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s ease;
  }

  &::after {
    content: "";
    position: absolute;
    left: 0;
    top: 18px;
    bottom: 18px;
    width: 2px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.68);
    opacity: 0.24;
    pointer-events: none;
  }

  &:hover,
  &:focus-visible {
    border-color: rgba(255, 255, 255, 0.18);
    background:
      radial-gradient(circle at 0% 0%, rgba(255, 255, 255, 0.075), rgba(255, 255, 255, 0) 38%),
      rgba(255, 255, 255, 0.05);
  }

  &:hover::before,
  &:focus-visible::before {
    opacity: 1;
  }

  &:hover [data-priority-visual],
  &:focus-visible [data-priority-visual] {
    transform: translateX(2px);
    color: rgba(255, 255, 255, 0.95);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.34);
    outline-offset: 3px;
  }

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr) 48px;
    gap: 12px;
    min-height: 86px;
    padding: 14px;

    [data-priority-visual] {
      width: 48px;
      height: 48px;
    }
  }

  @media (max-width: 350px) {
    grid-template-columns: 1fr;

    [data-priority-visual] {
      justify-self: stretch;
      width: 100%;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    &::before,
    [data-priority-visual] {
      transition: none !important;
    }
  }
`;

const PillarsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;

  @media (hover: hover) and (pointer: fine) {
    &:hover [data-pillar-visual="true"] {
      --motion-play-state: paused;
    }

    &:hover [data-pillar-card]:hover [data-pillar-visual="true"],
    &:focus-within [data-pillar-card]:focus-visible [data-pillar-visual="true"] {
      --motion-play-state: running;
    }
  }

  @media (max-width: 1120px) {
    grid-template-columns: 1fr;
  }
`;

const PillarCard = styled(GlassPanel)`
  --pillar-card-pad: 20px;

  appearance: none;
  display: grid;
  gap: 18px;
  padding: var(--pillar-card-pad);
  border-radius: 30px;
  color: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.18);
  }
`;

const PillarVisualFrame = styled.div`
  width: calc(100% + (var(--pillar-card-pad) * 2));
  margin: calc(var(--pillar-card-pad) * -1)
    calc(var(--pillar-card-pad) * -1) 0;
  display: grid;
  justify-self: stretch;
  overflow: hidden;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.025);

  > * {
    min-width: 0;
  }
`;

const PillarMedia = styled(MarketingMediaSlot)`
  width: 100%;
  max-width: none;
  margin: 0;
  border-radius: 28px;
`;

const PillarVisual = styled(AboutWorkVisual)`
  width: 100%;
  max-width: none;
  margin: 0;
  border-radius: 28px;
`;

const PillarBody = styled.div`
  display: grid;
  gap: 10px;
`;

const PillarLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.56);
`;

const PillarTitle = styled.h3`
  margin: 0;
  font-size: clamp(32px, 3.5vw, 42px);
  line-height: 1.02;
`;

const PillarText = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 1.72;
  color: rgba(255, 255, 255, 0.8);
`;

const PillarOutcome = styled.p`
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.64);
`;

const PillarLink = styled.span`
  margin-top: 6px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.92);
`;

const CredibilityPanel = styled(GlassPanel)`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(360px, 1.05fr);
  gap: clamp(26px, 4vw, 58px);
  padding: clamp(26px, 3.5vw, 42px);
  border-radius: 30px;
  background:
    radial-gradient(circle at 10% 14%, rgba(255, 255, 255, 0.08), transparent 32%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.065), rgba(255, 255, 255, 0.018)),
    rgba(7, 8, 10, 0.56);

  &::before {
    content: "";
    position: absolute;
    inset: 1px;
    border-radius: inherit;
    border: 1px solid rgba(255, 255, 255, 0.045);
    pointer-events: none;
  }

  @media (max-width: 1040px) {
    grid-template-columns: 1fr;
  }
`;

const CredibilityCopy = styled.div`
  display: grid;
  gap: clamp(14px, 1.8vw, 20px);
  align-content: start;

  ${SectionLead} {
    max-width: 50rem;
  }
`;

const CredibilityRouteVisual = styled.div`
  width: min(100%, 520px);
  margin-top: clamp(8px, 1.5vw, 18px);
  display: grid;
  gap: 8px;
  padding: clamp(14px, 2vw, 18px);
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02)),
    rgba(255, 255, 255, 0.025);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07);

  svg {
    width: 100%;
    height: auto;
    overflow: visible;
  }

  .route-track,
  .route-flow,
  .route-backlog-mark,
  .route-live-mark,
  .route-system-mark {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    vector-effect: non-scaling-stroke;
  }

  .route-track {
    stroke: rgba(255, 255, 255, 0.16);
    stroke-width: 5;
  }

  .route-flow {
    stroke: rgba(206, 236, 244, 0.88);
    stroke-width: 5;
    stroke-dasharray: 34 156;
    animation: ${credibilityRouteFlow} 4.2s ease-in-out infinite;
  }

  .route-node {
    fill: rgba(255, 255, 255, 0.065);
    stroke: rgba(255, 255, 255, 0.42);
    stroke-width: 3;
    vector-effect: non-scaling-stroke;
  }

  .route-node-b {
    fill: rgba(206, 236, 244, 0.22);
    stroke: rgba(206, 236, 244, 0.8);
  }

  .route-live-mark {
    stroke: rgba(255, 255, 255, 0.92);
    stroke-width: 3.2;
    stroke-dasharray: 34;
    animation: ${credibilityLineDraw} 4.2s ease-in-out infinite;
  }

  .route-backlog-mark,
  .route-system-mark {
    stroke: rgba(255, 255, 255, 0.52);
    stroke-width: 2.6;
  }

  .route-system-mark {
    stroke: rgba(206, 236, 244, 0.74);
    stroke-dasharray: 18;
    animation: ${credibilityLineDraw} 4.2s ease-in-out infinite 1.2s;
  }

  @media (prefers-reduced-motion: reduce) {
    .route-flow,
    .route-live-mark,
    .route-system-mark {
      animation: none !important;
    }
  }
`;

const CredibilityRouteLabels = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  span {
    min-width: 0;
    font-size: clamp(10px, 1vw, 12px);
    line-height: 1.2;
    font-weight: 800;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.68);
    text-wrap: balance;
  }

  span:nth-child(2) {
    text-align: center;
    color: rgba(255, 255, 255, 0.88);
  }

  span:nth-child(3) {
    text-align: right;
  }
`;

const CredibilityList = styled.div`
  display: grid;
  gap: 12px;
  align-content: center;
`;

const CredibilityItem = styled.div`
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: clamp(14px, 1.8vw, 20px);
  align-items: center;
  min-height: 128px;
  padding: clamp(16px, 2vw, 20px);
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.018)),
    rgba(255, 255, 255, 0.025);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition: transform 0.22s ease, border-color 0.22s ease, background 0.22s ease;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(206, 236, 244, 0.12), transparent 42%);
    opacity: 0;
    transition: opacity 0.22s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(255, 255, 255, 0.18);
    background:
      linear-gradient(145deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.022)),
      rgba(255, 255, 255, 0.03);
  }

  &:hover::after {
    opacity: 1;
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
    align-items: start;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none !important;

    &:hover {
      transform: none;
    }
  }
`;

const CredibilityStepMeta = styled.div`
  position: relative;
  z-index: 1;
  width: 76px;
  display: grid;
  gap: 8px;
  justify-items: center;

  @media (max-width: 620px) {
    width: auto;
    grid-template-columns: auto auto;
    justify-content: start;
    align-items: center;
  }
`;

const CredibilityStepNumber = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.46);
`;

const CredibilityVisual = styled.span`
  width: 58px;
  height: 58px;
  display: grid;
  place-items: center;
  color: rgba(255, 255, 255, 0.78);

  svg {
    width: 54px;
    height: 54px;
    overflow: visible;
  }

  path,
  rect {
    vector-effect: non-scaling-stroke;
  }

  .credibility-frame,
  .credibility-route-muted,
  .credibility-route-live,
  .credibility-arrow,
  .credibility-proof-check,
  .credibility-sync-track,
  .credibility-sync-live,
  .credibility-system-line {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .credibility-frame,
  .credibility-route-muted,
  .credibility-sync-track {
    stroke: rgba(255, 255, 255, 0.22);
    stroke-width: 3;
  }

  .credibility-route-live,
  .credibility-proof-check,
  .credibility-sync-live {
    stroke: rgba(255, 255, 255, 0.86);
    stroke-width: 3.5;
  }

  .credibility-route-live {
    stroke-dasharray: 78;
    animation: ${credibilityLineDraw} 4s ease-in-out infinite;
  }

  .credibility-arrow {
    stroke: rgba(206, 236, 244, 0.82);
    stroke-width: 3.2;
  }

  .credibility-proof-bar {
    fill: rgba(206, 236, 244, 0.74);
    transform-box: fill-box;
    transform-origin: center bottom;
    animation: ${credibilityBarLift} 3.7s ease-in-out infinite;
  }

  .credibility-proof-bar-b {
    animation-delay: 0.18s;
  }

  .credibility-proof-bar-c {
    animation-delay: 0.36s;
  }

  .credibility-proof-check {
    stroke-dasharray: 34;
    animation: ${credibilityLineDraw} 4s ease-in-out infinite;
  }

  .credibility-system {
    fill: rgba(255, 255, 255, 0.075);
    stroke: rgba(255, 255, 255, 0.42);
    stroke-width: 2.4;
  }

  .credibility-system-line {
    stroke: rgba(255, 255, 255, 0.42);
    stroke-width: 2;
  }

  .credibility-sync-live {
    stroke-dasharray: 8 14;
    animation: ${credibilitySyncMove} 2.4s linear infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      animation: none !important;
    }
  }
`;

const CredibilityItemCopy = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 7px;
`;

const CredibilityTitle = styled.h3`
  margin: 0;
  font-size: clamp(22px, 2.2vw, 28px);
  line-height: 1.05;
`;

const CredibilityText = styled.p`
  margin: 0;
  max-width: 46rem;
  font-size: 15px;
  line-height: 1.62;
  color: rgba(255, 255, 255, 0.78);
`;

const ProofStack = styled.div`
  display: grid;
  gap: 28px;
`;

const ProofRow = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(280px, 0.95fr);
  gap: clamp(24px, 3vw, 42px);
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  text-align: left;
  cursor: pointer;

  ${(p) =>
    p.$reverse
      ? `
    direction: rtl;
    > * {
      direction: ltr;
    }
  `
      : ""}

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    direction: ltr;
  }
`;

const ProofMedia = styled(MarketingMediaSlot)`
  border-radius: 30px;
`;

const ProofCopy = styled.div`
  display: grid;
  gap: 14px;
`;

const ProofLabelRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const ProofLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.56);
`;

const ProofExternal = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
`;

const ProofTitle = styled.h3`
  margin: 0;
  font-size: clamp(34px, 4vw, 50px);
  line-height: 0.98;
`;

const ProofText = styled.p`
  margin: 0;
  max-width: 34rem;
  font-size: 17px;
  line-height: 1.74;
  color: rgba(255, 255, 255, 0.8);
`;

const FinalPanel = styled(GlassPanel)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: clamp(24px, 3vw, 40px);
  padding: clamp(28px, 4vw, 40px);
  border-radius: 32px;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    align-items: start;
  }
`;

const FinalCopy = styled.div`
  display: grid;
  gap: 14px;
  max-width: 42rem;
`;

const FinalHeading = styled.h3`
  margin: 0;
  font-size: clamp(34px, 4vw, 50px);
  line-height: 1;
`;

const FinalText = styled.p`
  margin: 0;
  font-size: 18px;
  line-height: 1.72;
  color: rgba(255, 255, 255, 0.8);
`;

const FinalActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  justify-content: flex-start;
`;
